import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { getAllBlogPosts, getRecentPosts, getAllCategories } from '../firebase/models/blogService';
import BlogSidebar from '../components/blog/BlogSidebar';

const BlogContainer = styled.div`
  padding: var(--spacing-xxl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: var(--spacing-xl);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const PageTitle = styled.h1`
  margin-bottom: var(--spacing-xl);
  
  span {
    color: var(--accent-blue);
  }
`;

const BlogGrid = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 20px;
`;

const BlogCard = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  width: calc(50% - 10px);
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const BlogImage = styled.div`
  height: 200px;
  background-image: url(${props => props.image || '/placeholder-image.jpg'});
  background-size: cover;
  background-position: center;
`;

const BlogContent = styled.div`
  padding: var(--spacing-lg);
`;

const BlogTitle = styled.h2`
  margin-top: 0;
  margin-bottom: var(--spacing-md);
  font-size: 1.5rem;
  
  a {
    color: inherit;
    text-decoration: none;
    
    &:hover {
      color: var(--accent-blue);
    }
  }
`;

const BlogMeta = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  color: #666;
  font-size: 0.9rem;
  margin-bottom: var(--spacing-md);
`;

const BlogExcerpt = styled.p`
  margin-bottom: var(--spacing-md);
  color: #444;
`;

const ReadMoreLink = styled(Link)`
  display: inline-block;
  color: var(--accent-blue);
  font-weight: 500;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--spacing-xl) 0;
  
  p {
    margin-bottom: var(--spacing-lg);
    color: #666;
  }
`;

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const truncate = (text, length = 150) => {
  if (!text) return '';
  return text.length > length ? text.substring(0, length) + '...' : text;
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  
  console.log("Blog component loaded - updated version with horizontal cards");
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch blog posts
        const postsData = await getAllBlogPosts();
        console.log("Retrieved blog posts:", postsData);
        setPosts(postsData.filter(post => post.published));
        
        // Fetch recent posts for sidebar
        const recentPostsData = await getRecentPosts(5);
        setRecentPosts(recentPostsData.filter(post => post.published));
        
        // Fetch categories for sidebar
        const categoriesData = await getAllCategories();
        setCategories(categoriesData);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching blog data:', err);
        setError('Failed to load blog posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [location.pathname]);
  
  return (
    <BlogContainer>
      <Container>
        <MainContent>
          <PageTitle>Notes from the <span>Lab</span></PageTitle>
          
          {loading ? (
            <p>Loading posts...</p>
          ) : error ? (
            <p>{error}</p>
          ) : posts.length === 0 ? (
            <EmptyState>
              <p>No blog posts found. Check back soon for new content!</p>
            </EmptyState>
          ) : (
            <BlogGrid>
              {posts.map(post => (
                <BlogCard key={post.id}>
                  <BlogImage image={post.featuredImage} />
                  <BlogContent>
                    <BlogTitle>
                      <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                    </BlogTitle>
                    <BlogMeta>
                      <span>{formatDate(post.createdAt)}</span>
                    </BlogMeta>
                    <BlogExcerpt>{truncate(post.excerpt || post.content)}</BlogExcerpt>
                    <ReadMoreLink to={`/blog/${post.slug}`}>Read More</ReadMoreLink>
                  </BlogContent>
                </BlogCard>
              ))}
            </BlogGrid>
          )}
        </MainContent>
        
        <BlogSidebar 
          recentPosts={recentPosts} 
          categories={categories}
        />
      </Container>
    </BlogContainer>
  );
};

export default Blog; 